<template>
  <div
    dir="rtl"
    class="arad-surface aradcore-shell min-h-screen"
  >
    <UDashboardGroup
      unit="px"
      :storage-key="storageKey"
    >
      <UDashboardSidebar
        collapsible
        side="right"
        toggle-side="right"
        :menu="{ side: 'right' }"
        :default-size="280"
        :min-size="240"
        :max-size="320"
        :collapsed-size="64"
        :ui="{ root: 'bg-[var(--arad-surface)] border-[var(--ui-border)]' }"
      >
        <template #header="{ collapsed }">
          <div
            class="flex min-w-0 flex-1 items-center gap-2"
            :class="collapsed ? 'justify-center' : ''"
          >
            <div class="aradcore-logo flex size-9 shrink-0 items-center justify-center rounded-xl text-white">
              <UIcon
                :name="brandIcon"
                class="size-4"
              />
            </div>
            <div
              v-if="!collapsed"
              class="min-w-0"
            >
              <p class="aradcore-brand truncate text-sm font-semibold">
                AradCore
              </p>
              <p class="truncate text-xs text-[var(--ui-text-muted)]">
                {{ brandSubtitle }}
              </p>
            </div>
          </div>
        </template>

        <template #default="{ collapsed }">
          <UDashboardSearchButton
            v-if="!hideSearch"
            :collapsed="collapsed"
            :class="collapsed ? 'mx-auto' : 'w-full'"
            :kbds="[]"
            label="جستجو…"
          />
          <UNavigationMenu
            :collapsed="collapsed"
            :items="navItems as never"
            orientation="vertical"
            highlight
            class="w-full"
          />
        </template>

        <template #footer="{ collapsed }">
          <div :class="collapsed ? 'flex justify-center' : 'w-full'">
            <slot
              name="user-menu"
              :collapsed="collapsed"
              placement="sidebar"
            />
          </div>
        </template>
      </UDashboardSidebar>

      <UDashboardSearch
        v-if="!hideSearch"
        :groups="searchGroups as never"
        placeholder="جستجوی صفحات…"
        :color-mode="false"
      />

      <UDashboardPanel
        :ui="flush
          ? { body: 'flex min-h-0 flex-1 flex-col gap-0 overflow-hidden p-0' }
          : { body: 'flex flex-col gap-4 sm:gap-6 flex-1 overflow-y-auto px-4 pt-4 pb-16 sm:px-6 sm:pt-6 sm:pb-20' }"
      >
        <template #header>
          <UDashboardNavbar>
            <template #leading>
              <UBreadcrumb :items="breadcrumbs" />
            </template>
            <template #right>
              <AppColorModeToggle />
              <slot name="navbar-actions" />
              <div
                v-if="!hideNavbarUserMenu"
                class="hidden sm:block"
              >
                <slot
                  name="user-menu"
                  :collapsed="false"
                  placement="navbar"
                />
              </div>
            </template>
          </UDashboardNavbar>
        </template>

        <template #body>
          <div
            class="relative"
            :class="flush
              ? 'flex min-h-0 flex-1 flex-col overflow-hidden'
              : 'flex min-h-[50vh] flex-1 flex-col px-4 pt-4 pb-20 sm:px-6 sm:pt-6 sm:pb-24'"
          >
            <div
              v-show="isNavigating"
              class="absolute inset-0 z-50 flex items-center justify-center bg-[var(--ui-bg)]/90 backdrop-blur-sm"
              aria-live="polite"
              aria-busy="true"
              role="status"
            >
              <div class="flex flex-col items-center gap-3">
                <UIcon
                  name="i-lucide-loader-circle"
                  class="size-8 animate-spin text-primary"
                />
                <p class="text-sm text-[var(--ui-text-muted)]">
                  در حال بارگذاری…
                </p>
              </div>
            </div>
            <div
              :class="flush
                ? ['flex min-h-0 flex-1 flex-col overflow-hidden', isNavigating ? 'invisible' : '']
                : ['flex-1', isNavigating ? 'invisible' : '']"
            >
              <slot />
            </div>
            <footer class="mt-4 flex shrink-0 flex-col gap-1 border-t border-[var(--ui-border)] py-4 text-center text-xs text-[var(--ui-text-muted)] sm:mt-6 sm:flex-row sm:items-center sm:justify-between sm:text-start">
              <span><span class="font-semibold text-[var(--arad-text)]">AradCore</span> — پلتفرم طراحی سایت‌های هوشمند</span>
              <span>© {{ copyrightYear }} AradCore — تمامی حقوق محفوظ است.</span>
            </footer>
            <div
              v-if="tabBarItems?.length"
              aria-hidden="true"
              class="h-24 shrink-0 lg:hidden"
              style="height: calc(6rem + env(safe-area-inset-bottom))"
            />
          </div>
        </template>
      </UDashboardPanel>
    </UDashboardGroup>

    <AppMobileTabBar
      v-if="tabBarItems?.length"
      :items="tabBarItems"
    />

    <div
      v-if="siteSwitching"
      class="fixed inset-0 z-[80] flex items-center justify-center bg-[var(--ui-bg)]/90 backdrop-blur-sm"
      aria-live="polite"
      aria-busy="true"
      role="status"
    >
      <div class="flex flex-col items-center gap-3">
        <UIcon
          name="i-lucide-loader-circle"
          class="size-8 animate-spin text-primary"
        />
        <p class="text-sm text-[var(--ui-text-muted)]">
          در حال بارگذاری…
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { MobileTabItem } from '~/components/kit/MobileTabBar.vue'

withDefaults(
  defineProps<{
    storageKey: string
    brandSubtitle: string
    brandIcon?: string
    navItems: unknown
    searchGroups: unknown
    breadcrumbs: Array<{ label: string; to?: string }>
    tabBarItems?: MobileTabItem[]
    flush?: boolean
    hideSearch?: boolean
    hideNavbarUserMenu?: boolean
  }>(),
  {
    brandIcon: 'i-lucide-sparkles',
    tabBarItems: undefined,
    flush: false,
    hideSearch: false,
    hideNavbarUserMenu: false,
  },
)

usePersianLayout()

const { isLoading: isNavigating } = usePanelRouteLoading()
const siteSwitching = useState('site-switch-loading', () => false)
const copyrightYear = new Intl.NumberFormat('fa-IR', { useGrouping: false }).format(new Date().getFullYear())
</script>
